import React from "react";
import { Facebook, Instagram, Twitter, Youtube, Linkedin, Globe } from "lucide-react";

/**
 * SocialLinks — affiche une rangée d'icônes vers les réseaux sociaux du club.
 *
 * Règles :
 *   - n'affiche QUE les icônes pour lesquelles un lien est renseigné
 *   - sources fusionnées : `teamInfo.links` (scrap LNH) + `settings` (override admin)
 *   - icônes Lucide quand dispo, SVG inline sinon (TikTok, Threads, Snapchat)
 *
 * Usage :
 *   <SocialLinks links={teamInfo.links} settings={settings} className="mt-6 flex gap-2"/>
 */

// SVG icons for networks not in lucide
const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z"/>
  </svg>
);
const ThreadsIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.5 11.6c-.1-.1-.3-.1-.4-.2-.2-3-1.9-4.7-4.6-4.7h-.1c-1.6 0-3 .7-3.8 1.9l1.5 1c.6-.9 1.5-1.1 2.3-1.1 1 0 1.8.3 2.3.9.4.4.6 1 .8 1.7-1-.2-2.1-.2-3.3-.1-3.3.2-5.5 2.1-5.3 4.8.1 1.4.8 2.6 2 3.4.9.7 2.3.9 3.7.9 1.7-.1 3.1-.8 4-2 .7-.9 1.1-2.1 1.3-3.6 1 .6 1.7 1.3 2.1 2.3.7 1.6.8 4.2-1.4 6.4-1.9 1.9-4.2 2.7-7 2.7-3.1 0-5.5-1-7.1-3-1.5-1.9-2.3-4.6-2.3-8.1 0-3.5.8-6.2 2.3-8.1C5 3.4 7.4 2.4 10.5 2.4c3.2 0 5.6 1 7.2 3 .8 1 1.4 2.1 1.8 3.5l1.7-.5c-.5-1.6-1.2-3-2.2-4.2C17.1 1.7 14.2.5 10.5.5c-3.7 0-6.6 1.2-8.5 3.7C.3 6.4-.5 9.6-.5 13.5s.8 7.1 2.5 9.3c1.9 2.5 4.8 3.7 8.5 3.7 3.4 0 5.7-.9 8.4-3.4 1.3-1.3 2.4-3.2 2.6-5.2.2-2.6-.5-4.7-2-6.3zm-5.4 6c-1.3.1-2.7-.5-2.8-1.7-.1-1 .9-2 2.7-2.1.4 0 .8 0 1.1 0 .8 0 1.6.1 2.3.2-.2 2.1-1.2 3.5-3.3 3.6z"/>
  </svg>
);
const SnapchatIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2c2.7 0 5.1 1.5 6.1 4 .4 1 .3 2.4.2 3.5v.4c0 .2 0 .4.1.5.2.2.5.3.7.4l.2.1c.6.2 1.5.5 1.7 1.1.2.6-.6 1.2-1.6 1.7-.1 0-.2.1-.4.2-.7.4-1.7.9-2.1 1.7-.1.2-.1.5-.1.7v.1c0 .1 0 .3-.1.4-.2.2-.6.3-1 .3h-.3c-.5-.1-1 0-1.6.1-.5.1-1 .3-1.5.7-.7.6-1.4.9-2.3.9s-1.6-.3-2.3-.9c-.5-.4-1-.6-1.5-.7-.6-.1-1.1-.2-1.6-.1H4.4c-.5 0-.8-.1-1-.3-.1-.1-.1-.3-.1-.4v-.1c0-.2 0-.5-.1-.7-.4-.8-1.4-1.3-2.1-1.7-.2-.1-.3-.1-.4-.2-1-.5-1.8-1.1-1.6-1.7.2-.6 1.1-.9 1.7-1.1l.2-.1c.2-.1.5-.2.7-.4.1-.1.1-.3.1-.5v-.4c-.1-1.1-.2-2.5.2-3.5C6.9 3.5 9.3 2 12 2z"/>
  </svg>
);

const NETWORK_DEFS = [
  { key: "facebook", label: "Facebook", Icon: ({ size }) => <Facebook size={size}/> },
  { key: "instagram", label: "Instagram", Icon: ({ size }) => <Instagram size={size}/> },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon },
  { key: "twitter", label: "X (Twitter)", Icon: ({ size }) => <Twitter size={size}/> },
  { key: "x", label: "X", Icon: ({ size }) => <Twitter size={size}/> },
  { key: "youtube", label: "YouTube", Icon: ({ size }) => <Youtube size={size}/> },
  { key: "linkedin", label: "LinkedIn", Icon: ({ size }) => <Linkedin size={size}/> },
  { key: "threads", label: "Threads", Icon: ThreadsIcon },
  { key: "snapchat", label: "Snapchat", Icon: SnapchatIcon },
  { key: "website", label: "Site officiel", Icon: ({ size }) => <Globe size={size}/> },
];

const STYLE_DEFAULT = "w-10 h-10 rounded-full bg-white/10 hover:bg-pink-500 hover:text-white border border-white/15 hover:border-pink-400 flex items-center justify-center transition shadow-lg";

export default function SocialLinks({
  links = {},
  settings = {},
  className = "flex gap-2 flex-wrap",
  iconSize = 16,
  buttonClassName = STYLE_DEFAULT,
  excludeKeys = [],
}) {
  // Merge sources : settings > teamInfo.links (admin can override)
  const merged = { ...links };
  ["facebook", "instagram", "tiktok", "twitter", "youtube", "linkedin", "threads", "snapchat", "website"].forEach((k) => {
    const s = settings?.[`social_${k}`] || settings?.[k];
    if (s && typeof s === "string") merged[k] = s;
  });

  // De-dup keys (don't render both `twitter` and `x`)
  const seen = new Set();
  const items = NETWORK_DEFS
    .filter((n) => !excludeKeys.includes(n.key))
    .filter((n) => {
      const url = (merged[n.key] || "").trim();
      if (!url) return false;
      // Same url already rendered (twitter/x duplicate) → skip
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });

  if (!items.length) return null;

  return (
    <div className={className} data-testid="social-links">
      {items.map((n) => (
        <a
          key={n.key}
          href={merged[n.key]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={n.label}
          title={n.label}
          className={buttonClassName}
          data-testid={`social-${n.key}`}
        >
          <n.Icon size={iconSize}/>
        </a>
      ))}
    </div>
  );
}
